import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';

const ChartCard = styled.div`
  background: ${theme.colors.doryBeige};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const ChartToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing.md};
`;

const ToggleGroup = styled.div`
  display: inline-flex;
  border: 1px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
`;

const ToggleButton = styled.button`
  background: ${(props) => (props.$active ? theme.colors.primary : 'transparent')};
  color: ${(props) => (props.$active ? 'white' : theme.colors.doryDarkerBlue)};
  border: none;
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  line-height: 1.3;
  transition: background ${theme.animation.durations.fast}, color ${theme.animation.durations.fast};

  &:hover {
    background: ${(props) => (props.$active ? theme.colors.primary : theme.colors.doryBeige)};
    color: ${(props) => (props.$active ? 'white' : theme.colors.doryBlue)};
  }

  & + & {
    border-left: 1px solid ${theme.colors.doryLighterBlue};
  }
`;

const ChartContainer = styled.div`
  height: 400px;
  display: flex;
  align-items: end;
  justify-content: space-between;
  padding: ${theme.spacing.md} 0;
  border-bottom: 2px solid ${theme.colors.doryLighterBlue};
  margin-bottom: ${theme.spacing.md};
`;

const BarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: ${(props) => props.$maxWidth || '48px'};
`;

const Bar = styled.div`
  width: 100%;
  background: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm} ${theme.borderRadius.sm} 0 0;
  min-height: 4px;
  margin-bottom: ${theme.spacing.lg};
  transition: all ${theme.animation.durations.fast};

  &:hover {
    opacity: 0.8;
  }
`;

const BarLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  white-space: nowrap;
  margin-top: ${theme.spacing.sm};
`;

const BarValue = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  text-align: center;
  margin-bottom: ${theme.spacing.xs};
`;

const CurveChartShell = styled.div`
  border-bottom: 2px solid ${theme.colors.doryLighterBlue};
  margin-bottom: ${theme.spacing.md};
`;

const CurveSvgWrap = styled.div`
  height: 360px;
  width: 100%;
  padding: ${theme.spacing.md} 0 ${theme.spacing.lg};
`;

const CurveSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
`;

const CurveLabels = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing.sm} 0 ${theme.spacing.md};
`;

const CurveLabel = styled.div`
  flex: 1;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-align: center;
  white-space: nowrap;
`;

const CHART_PLOT_HEIGHT = 350;
const CURVE_STROKE_WIDTH = 10;
const CURVE_SAMPLE_COUNT = 120;

const getBarHeight = (value, maxValue) => {
  if (maxValue === 0) return 4;
  return Math.max(4, (value / maxValue) * CHART_PLOT_HEIGHT);
};

/**
 * Least-squares polynomial fit. Returns coefficients [a0, a1, ..., adeg]
 * for y ≈ a0 + a1*x + a2*x^2 + ...
 */
const fitPolynomial = (xs, ys, degree) => {
  const n = xs.length;
  const deg = Math.max(1, Math.min(degree, n - 1));
  const m = deg + 1;

  // Normal equations: (A^T A) c = A^T y
  const ata = Array.from({ length: m }, () => Array(m).fill(0));
  const aty = Array(m).fill(0);

  for (let i = 0; i < n; i++) {
    const x = xs[i];
    const y = ys[i];
    const powers = Array(m).fill(1);
    for (let j = 1; j < m; j++) {
      powers[j] = powers[j - 1] * x;
    }
    for (let r = 0; r < m; r++) {
      aty[r] += powers[r] * y;
      for (let c = 0; c < m; c++) {
        ata[r][c] += powers[r] * powers[c];
      }
    }
  }

  // Gaussian elimination with partial pivoting
  const aug = ata.map((row, i) => [...row, aty[i]]);
  for (let col = 0; col < m; col++) {
    let pivot = col;
    for (let row = col + 1; row < m; row++) {
      if (Math.abs(aug[row][col]) > Math.abs(aug[pivot][col])) {
        pivot = row;
      }
    }
    if (pivot !== col) {
      const tmp = aug[col];
      aug[col] = aug[pivot];
      aug[pivot] = tmp;
    }
    const diag = aug[col][col];
    if (Math.abs(diag) < 1e-12) {
      continue;
    }
    for (let row = col + 1; row < m; row++) {
      const factor = aug[row][col] / diag;
      for (let j = col; j <= m; j++) {
        aug[row][j] -= factor * aug[col][j];
      }
    }
  }

  const coeffs = Array(m).fill(0);
  for (let i = m - 1; i >= 0; i--) {
    let sum = aug[i][m];
    for (let j = i + 1; j < m; j++) {
      sum -= aug[i][j] * coeffs[j];
    }
    coeffs[i] = Math.abs(aug[i][i]) < 1e-12 ? 0 : sum / aug[i][i];
  }

  return coeffs;
};

const evaluatePolynomial = (coeffs, x) => {
  let y = 0;
  let power = 1;
  for (let i = 0; i < coeffs.length; i++) {
    y += coeffs[i] * power;
    power *= x;
  }
  return y;
};

const buildBestFitPath = (values, maxCount) => {
  const n = values.length;
  if (n === 0 || maxCount <= 0) {
    return '';
  }

  const xs = values.map((_, i) => i);
  const ys = values.map((v) => Number(v) || 0);
  // Degree scales with bucket count; enough to follow the shape without wild oscillation
  const degree = Math.min(5, Math.max(2, Math.floor(n / 3)));
  const coeffs = fitPolynomial(xs, ys, degree);

  const padX = 0.5;
  const width = Math.max(1, n - 1) + padX * 2;
  const height = CHART_PLOT_HEIGHT;
  const topPad = 8;
  const bottomPad = 24;
  const usableHeight = height - topPad - bottomPad;

  const toX = (x) => ((x + padX) / width) * 1000;
  const toY = (count) => {
    const clamped = Math.max(0, count);
    const barH = maxCount === 0 ? 0 : (clamped / maxCount) * usableHeight;
    return height - bottomPad - barH;
  };

  const parts = [];
  for (let i = 0; i <= CURVE_SAMPLE_COUNT; i++) {
    const t = i / CURVE_SAMPLE_COUNT;
    const x = (n - 1) * t;
    const y = evaluatePolynomial(coeffs, x);
    const px = toX(x);
    const py = toY(y);
    parts.push(`${i === 0 ? 'M' : 'L'} ${px.toFixed(2)} ${py.toFixed(2)}`);
  }

  return parts.join(' ');
};

/**
 * Distribution chart with Bars / Curve toggle.
 * `distribution` is an object keyed by bucket label (e.g. "0".."10") with counts.
 */
const DistributionChart = ({ distribution, maxCount }) => {
  const [viewMode, setViewMode] = useState('bars');

  const buckets = useMemo(
    () => Object.keys(distribution || {}).sort((a, b) => Number(a) - Number(b)),
    [distribution]
  );

  const values = useMemo(
    () => buckets.map((key) => distribution[key] || 0),
    [buckets, distribution]
  );

  const curvePath = useMemo(
    () => buildBestFitPath(values, maxCount),
    [values, maxCount]
  );

  const barMaxWidth = buckets.length > 12 ? '30px' : '48px';

  return (
    <ChartCard>
      <ChartToolbar>
        <ToggleGroup role="group" aria-label="Chart view">
          <ToggleButton
            type="button"
            $active={viewMode === 'bars'}
            onClick={() => setViewMode('bars')}
          >
            Bars
          </ToggleButton>
          <ToggleButton
            type="button"
            $active={viewMode === 'curve'}
            onClick={() => setViewMode('curve')}
          >
            Curve
          </ToggleButton>
        </ToggleGroup>
      </ChartToolbar>

      {viewMode === 'bars' ? (
        <ChartContainer>
          {buckets.map((key) => (
            <BarContainer key={key} $maxWidth={barMaxWidth}>
              <BarValue>{distribution[key]}</BarValue>
              <Bar
                style={{
                  height: `${getBarHeight(distribution[key], maxCount)}px`
                }}
              />
              <BarLabel>{parseInt(key, 10)}</BarLabel>
            </BarContainer>
          ))}
        </ChartContainer>
      ) : (
        <CurveChartShell>
          <CurveSvgWrap>
            <CurveSvg viewBox={`0 0 1000 ${CHART_PLOT_HEIGHT}`} preserveAspectRatio="none">
              {curvePath && (
                <path
                  d={curvePath}
                  fill="none"
                  stroke={theme.colors.primary}
                  strokeWidth={CURVE_STROKE_WIDTH}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            </CurveSvg>
          </CurveSvgWrap>
          <CurveLabels>
            {buckets.map((key) => (
              <CurveLabel key={key}>{parseInt(key, 10)}</CurveLabel>
            ))}
          </CurveLabels>
        </CurveChartShell>
      )}
    </ChartCard>
  );
};

export default DistributionChart;
